import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "../css/StartupRecommendationOverlay.css";

const SPIN_DURATION = 3400;
const FAST_FRAME_DURATION = 90;
const SLOW_FRAME_DURATION = 220;
const SLOWDOWN_BEFORE_END = 900;

export default function StartupRecommendationOverlay({ open, items, getColor, copy, onClose }) {
    const intervalRef = useRef(null);
    const timeoutRef = useRef(null);
    const slowDownTimeoutRef = useRef(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [phase, setPhase] = useState('spinning');

    const safeItems = useMemo(() => items ?? [], [items]);
    const activeItem = safeItems[activeIndex] ?? null;
    const reelItems = useMemo(() => {
        if (safeItems.length === 0) {
            return [];
        }

        return Array.from({ length: 3 }, (_, loopIndex) =>
            safeItems.map((item) => ({
                ...item,
                reelId: `${loopIndex}-${item.id}`,
            }))
        ).flat();
    }, [safeItems]);

    useEffect(() => {
        if (!open || safeItems.length === 0) {
            return undefined;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const clearTimers = () => {
            if (intervalRef.current) {
                window.clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            if (timeoutRef.current) {
                window.clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            if (slowDownTimeoutRef.current) {
                window.clearTimeout(slowDownTimeoutRef.current);
                slowDownTimeoutRef.current = null;
            }
        };

        const handleKeydown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        const pickedFinalIndex = Math.floor(Math.random() * safeItems.length);
        const startIndex = Math.floor(Math.random() * safeItems.length);

        setPhase('spinning');
        setActiveIndex(startIndex);

        intervalRef.current = window.setInterval(() => {
            setActiveIndex((current) => (current + 1) % safeItems.length);
        }, FAST_FRAME_DURATION);

        slowDownTimeoutRef.current = window.setTimeout(() => {
            if (intervalRef.current) {
                window.clearInterval(intervalRef.current);
            }
            intervalRef.current = window.setInterval(() => {
                setActiveIndex((current) => (current + 1) % safeItems.length);
            }, SLOW_FRAME_DURATION);
        }, SPIN_DURATION - SLOWDOWN_BEFORE_END);

        timeoutRef.current = window.setTimeout(() => {
            clearTimers();
            setActiveIndex(pickedFinalIndex);
            setPhase('settled');
        }, SPIN_DURATION);

        window.addEventListener('keydown', handleKeydown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', handleKeydown);
            clearTimers();
        };
    }, [open, onClose, safeItems]);

    if (!open || !activeItem) {
        return null;
    }

    return createPortal(
        <div className="startup-overlay" onClick={onClose}>
            <div className="startup-overlay-panel" onClick={(event) => event.stopPropagation()}>
                <div className="startup-overlay-header">
                    <div className="startup-overlay-kicker">灵签起卦</div>
                    <button type="button" className="startup-overlay-close" onClick={onClose} aria-label="关闭推荐">
                        ×
                    </button>
                </div>
                <div className="startup-overlay-title">{copy.title}</div>
                <div className="startup-overlay-subtitle">
                    {phase === 'spinning' ? copy.spinningCopy : copy.resultCopy}
                </div>

                <div className={`startup-fortune-cup ${phase === 'spinning' ? 'startup-fortune-cup-shaking' : ''}`} aria-hidden="true">
                    <div className="startup-fortune-cup-rim" />
                    <div className="startup-fortune-stick startup-fortune-stick-1" />
                    <div className="startup-fortune-stick startup-fortune-stick-2" />
                    <div className="startup-fortune-stick startup-fortune-stick-3" />
                    <div className="startup-fortune-stick startup-fortune-stick-4" />
                    <div className="startup-fortune-stick startup-fortune-stick-5" />
                </div>

                <div className="startup-reel-shell">
                    <div className="startup-reel-pointer" aria-hidden="true" />
                    <div className="startup-reel-window">
                        <div
                            className={`startup-reel-track ${phase === 'spinning' ? 'startup-reel-track-spinning' : 'startup-reel-track-settled'}`}
                            style={{ transform: `translateY(calc(50% - (var(--startup-reel-step) * ${safeItems.length + activeIndex})))` }}
                        >
                            {reelItems.map((item, index) => (
                                <div
                                    key={item.reelId}
                                    className={`startup-reel-chip ${index % safeItems.length === activeIndex ? 'startup-reel-chip-active' : ''}`}
                                >
                                    <span className="startup-reel-chip-seal">签</span>
                                    <span className="startup-reel-chip-label">{item.categoryLabel}</span>
                                    <span className="startup-reel-chip-name">{item.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className={`startup-recommendation-card ${phase === 'spinning' ? 'startup-recommendation-card-spinning' : 'startup-recommendation-card-settled'}`}>
                    <div className="startup-recommendation-image-box">
                        <img className="startup-recommendation-image" src={activeItem.image} alt={activeItem.name} />
                        {activeItem.badge ? <span className="startup-recommendation-badge">{activeItem.badge}</span> : null}
                        <div className="startup-recommendation-glow" aria-hidden="true" />
                    </div>
                    <div className="startup-recommendation-meta">
                        <div className="startup-recommendation-category">{activeItem.categoryLabel}</div>
                        <div className="startup-recommendation-name" style={{ color: getColor(activeItem.star) }}>
                            {activeItem.name}
                        </div>
                        {activeItem.subtitle ? <div className="startup-recommendation-copy">{activeItem.subtitle}</div> : null}
                        <div className="startup-recommendation-stars" style={{ color: getColor(activeItem.star) }}>
                            {Array.from({ length: activeItem.star }).map((_, index) => (
                                <span key={index}>★</span>
                            ))}
                        </div>
                        {activeItem.tags?.length ? (
                            <div className="startup-recommendation-tags">
                                {activeItem.tags.slice(0, 3).map((tag) => (
                                    <span key={tag} className="startup-recommendation-tag">{tag}</span>
                                ))}
                            </div>
                        ) : null}
                        {phase === 'settled' ? (
                            <div className="startup-recommendation-result">签面已定，今日宜顺势而行。</div>
                        ) : (
                            <div className="startup-recommendation-result startup-recommendation-result-muted">签筒轻摇，签文浮现中...</div>
                        )}
                    </div>
                </div>

                <button type="button" className="startup-overlay-action" onClick={onClose}>
                    {copy.closeLabel}
                </button>
                <div className="startup-overlay-mobile-bar">
                    <button type="button" className="startup-overlay-mobile-action" onClick={onClose}>
                        {copy.closeLabel}
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
}
